// Market normalization layer — collapses the three legacy code families into the
// single canonical enum (FLG | PSCT | TUS). See REBUILD-SPEC.md §2.
//
// Legacy carried three code families that must be reconciled:
//   - product.region strings: Flagstaff | Prescott | Tucson
//   - UI codes (legacy REGION_CODE): FLG | PRSCT | TUS
//   - Notion / availability tour prefixes: FLG | PSCT | TUC
// Rules: Prescott => PSCT everywhere (legacy used PRSCT). Tucson TUC => TUS.

import type { Market, Region } from "@/types";
import { productById } from "@/config/products";

/**
 * Canonicalize any market-ish code or region label to the single enum.
 * Accepts region labels (Flagstaff/Prescott/Tucson), UI codes (FLG/PRSCT/TUS),
 * and Notion prefixes (FLG/PSCT/TUC). Case-insensitive.
 *
 * On an UNKNOWN code we THROW rather than guess: silent mis-mapping of a market
 * was a class of legacy bug (e.g. the TUS/TUC Team Mtg mislabeling), so failing
 * loud is the safer default. Callers that prefer a fallback should catch.
 */
export function canonMarket(anyCode: string): Market {
  const c = (anyCode || "").trim().toUpperCase();
  switch (c) {
    case "FLG":
    case "FLAGSTAFF":
      return "FLG";
    case "PSCT":
    case "PRSCT":
    case "PRESCOTT":
      return "PSCT";
    case "TUS":
    case "TUC":
    case "TUCSON":
      return "TUS";
    default:
      throw new Error(`canonMarket: unknown market code "${anyCode}"`);
  }
}

/**
 * Derive a product's market from its region — NEVER from a TOUR-name prefix.
 * Prefix-derivation is unsafe (REBUILD-SPEC §2.2): "TUS Team Mtg" vs "TUC ..."
 * rows in Notion prove the prefixes are inconsistent. Region is authoritative.
 * Throws (via canonMarket) for an unknown productId.
 */
export function marketOfProduct(productId: string): Market {
  const p = productById[productId];
  if (!p) throw new Error(`marketOfProduct: unknown productId "${productId}"`);
  return canonMarket(p.region);
}

/** Region label -> canonical Market. (Legacy REGION_CODE, with PRSCT -> PSCT.) */
export const REGION_CODE: Record<Region, Market> = {
  Flagstaff: "FLG",
  Prescott: "PSCT",
  Tucson: "TUS",
};
